import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Brain, CheckCircle2, XCircle, RotateCcw, ChevronLeft, Target, BookOpen, ExternalLink } from 'lucide-react';
import publicApi from '../lib/api';
import SourceAttribution from '../components/SourceAttribution';
import { privateStudyApi } from '../lib/studyApi';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';

export default function QuizPage({ preview = false }) {
  const { user } = useAuth();
  const isDemo = user?.role === 'PARTNER_DEMO';
  const api = preview ? privateStudyApi : publicApi;
  const routePrefix = preview ? '/admin/preview' : '';
  const { contestId } = useParams();
  const [contest, setContest] = useState(null);
  const [mode, setMode] = useState('contest');
  const [subjectId, setSubjectId] = useState('');
  const [limit, setLimit] = useState('10');
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get(isDemo && !preview ? `/library/${contestId}` : `/contests/${contestId}`).then(({ data }) => setContest(data)).catch(() => {});
    api.get(`/quiz/stats?contest_id=${contestId}`).then(({ data }) => setStats(data)).catch(() => {});
  }, [contestId, api, isDemo, preview]);

  const subjects = contest
    ? contest.index.tree.map((m) => [m.module_id, m.title])
    : [];

  const start = async () => {
    try {
      const params = new URLSearchParams({ mode, limit });
      params.set('contest_id', contestId);
      if (mode === 'subject' && subjectId) params.set('subject_id', subjectId);
      const { data } = await api.get(`/quiz/questions?${params}`);
      if (!data.questions.length) {
        toast.info(mode === 'wrong' ? 'Nessuna domanda sbagliata da ripassare. Ottimo!' : 'Nessuna domanda disponibile');
        return;
      }
      setQuestions(data.questions);
      setAnswers({});
      setResult(null);
    } catch { toast.error('Errore nel caricamento del quiz'); }
  };

  const submit = async () => {
    const payload = {
      scope: mode,
      scope_id: contestId,
      answers: questions.map((q) => ({ question_id: q.question_id, chosen_index: answers[q.question_id] })),
    };
    const { data } = await api.post('/quiz/submit', payload);
    setResult(data);
    api.get(`/quiz/stats?contest_id=${contestId}`).then(({ data }) => setStats(data)).catch(() => {});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const answeredCount = Object.keys(answers).length;
  const resultSources = Object.fromEntries((result?.sources || []).map((s) => [s.source_id, s]));

  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="quiz-page">
      {isDemo && <div className="mb-6 rounded-2xl border border-indigo-200 bg-indigo-50 px-5 py-4" role="status" data-testid="partner-demo-banner"><p className="text-xs font-bold tracking-[0.14em] text-brand">CORSO DIMOSTRATIVO PARTNER</p><p className="mt-1 text-sm text-slate-600">Questa area serve esclusivamente a mostrare il funzionamento di ConcorsiEssentials e non costituisce accesso a un corso commerciale completo.</p></div>}
      <Link to={preview ? `/admin/preview/concorsi/${contestId}` : "/area-personale"} className="text-xs font-semibold text-slate-500 hover:text-brand inline-flex items-center gap-1 mb-6" data-testid="quiz-back">
        <ChevronLeft size={14} strokeWidth={1.5} /> Libreria
      </Link>
      {preview && <p role="status" className="mb-4 bg-amber-100 p-3">Anteprima privata admin · Tentativi di collaudo separati</p>}
      <p className="eyebrow mb-3">Verifica la preparazione</p>
      <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.04em] font-bold text-brand-dark mb-2" data-testid="quiz-title">
        Quiz Essentials
      </h1>
      {contest && <p className="text-slate-500 mb-8">{contest.contest.title}</p>}

      {stats && stats.attempts > 0 && !questions && (
        <div className="grid grid-cols-3 gap-4 mb-8" data-testid="quiz-stats">
          {[
            { l: 'Quiz svolti', v: stats.attempts },
            { l: 'Domande risposte', v: stats.questions_answered },
            { l: 'Punteggio medio', v: stats.avg_score !== null ? `${stats.avg_score}%` : '—' },
          ].map((s) => (
            <div key={s.l} className="bg-white border border-slate-200 rounded-xl p-4 text-center">
              <p className="font-display font-bold text-xl text-slate-900">{s.v}</p>
              <p className="text-xs text-slate-500 mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      )}

      {!questions && (
        <div className="grid gap-6 md:grid-cols-[.8fr_1.2fr]">
          <div className="rounded-2xl bg-brand-dark p-7 text-white"><Target className="mb-8 text-indigo-300" size={28} /><h2 className="font-display text-2xl font-bold">Allenati con intenzione.</h2><p className="mt-3 text-sm leading-6 text-slate-300">Scegli l’intero concorso, una materia oppure torna soltanto sugli errori.</p></div>
        <div className="surface p-7 space-y-5" data-testid="quiz-setup">
          <div>
            <Label className="text-xs tracking-[0.1em] uppercase text-slate-500 font-semibold">Modalità</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="mt-1.5 h-12" data-testid="quiz-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="contest" data-testid="mode-contest">Intero concorso</SelectItem>
                <SelectItem value="subject" data-testid="mode-subject">Singola materia</SelectItem>
                <SelectItem value="wrong" data-testid="mode-wrong">Solo domande sbagliate</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mode === 'subject' && (
            <div>
              <Label className="text-xs tracking-[0.1em] uppercase text-slate-500 font-semibold">Materia</Label>
              <Select value={subjectId} onValueChange={setSubjectId}>
                <SelectTrigger className="mt-1.5 h-12" data-testid="quiz-subject"><SelectValue placeholder="Scegli la materia" /></SelectTrigger>
                <SelectContent>
                  {subjects.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs tracking-[0.1em] uppercase text-slate-500 font-semibold">Numero di domande</Label>
            <Select value={limit} onValueChange={setLimit}>
              <SelectTrigger className="mt-1.5 h-12" data-testid="quiz-limit"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['5', '10', '15', '20'].map((n) => <SelectItem key={n} value={n}>{n} domande</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={start} disabled={mode === 'subject' && !subjectId} data-testid="quiz-start"
            className="w-full h-12 rounded-full bg-brand hover:bg-brand-hover text-white font-semibold">
            <Brain size={16} strokeWidth={1.5} className="mr-2" /> Inizia il quiz
          </Button>
        </div>
        </div>
      )}

      {questions && result && (
        <div className="bg-white border border-slate-200 rounded-2xl p-7 mb-8 text-center" data-testid="quiz-result">
          <p className="text-xs tracking-[0.1em] uppercase text-slate-500 font-semibold mb-2">Risultato</p>
          <p className="font-display text-5xl font-bold text-slate-900" data-testid="quiz-score">
            {result.score}<span className="text-2xl text-slate-400">/{result.total}</span>
          </p>
          <p className="text-slate-500 mt-2">{Math.round(result.score / Math.max(result.total, 1) * 100)}% di risposte corrette</p>
          <Button onClick={() => { setQuestions(null); setResult(null); }} variant="outline" data-testid="quiz-restart"
            className="mt-5 rounded-full">
            <RotateCcw size={14} strokeWidth={1.5} className="mr-2" /> Nuovo quiz
          </Button>
        </div>
      )}

      {questions && (
        <div className="space-y-6">
          {!result && <div className="sticky top-[4.5rem] z-20 rounded-b-2xl border-x border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur"><div className="mb-2 flex justify-between text-xs font-semibold text-slate-500"><span>Avanzamento</span><span>{answeredCount}/{questions.length}</span></div><div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-brand transition-all" style={{ width: `${answeredCount / questions.length * 100}%` }} /></div></div>}
          {(result ? result.results : questions).map((q, qi) => {
            return (
              <div key={q.question_id} className="surface p-5 sm:p-7" data-testid={`question-${qi}`}>
                <p className="mb-3 text-xs font-bold uppercase tracking-[0.13em] text-brand">Domanda {qi + 1} di {questions.length}</p>
                <p className="font-display text-lg font-bold leading-7 text-brand-dark mb-5">{q.text}</p>
                <div className="space-y-2">
                  {q.options.map((opt, oi) => {
                    let cls = 'border-slate-200 hover:border-brand text-slate-700';
                    if (!result && answers[q.question_id] === oi) cls = 'border-brand bg-brand-light text-brand font-medium';
                    if (result) {
                      if (oi === q.correct_index) cls = 'border-emerald-300 bg-emerald-50 text-emerald-700 font-medium';
                      else if (oi === q.chosen_index) cls = 'border-rose-300 bg-rose-50 text-rose-700';
                      else cls = 'border-slate-200 text-slate-400';
                    }
                    return (
                      <button key={oi} disabled={!!result}
                        onClick={() => setAnswers((a) => ({ ...a, [q.question_id]: oi }))}
                        data-testid={`question-${qi}-option-${oi}`}
                        className={`w-full min-h-[52px] text-left text-sm border rounded-xl px-4 py-3 transition-colors flex items-center justify-between gap-3 ${cls}`}>
                        <span className="flex items-center gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-current/20 text-xs font-bold">{String.fromCharCode(65 + oi)}</span>{opt}</span>
                        {result && oi === q.correct_index && <CheckCircle2 size={16} strokeWidth={1.5} className="shrink-0" />}
                        {result && oi === q.chosen_index && oi !== q.correct_index && <XCircle size={16} strokeWidth={1.5} className="shrink-0" />}
                      </button>
                    );
                  })}
                </div>
                {result && q.explanation && (
                  <p className="mt-4 text-sm text-slate-600 bg-slate-50 rounded-xl px-4 py-3" data-testid={`explanation-${qi}`}>
                    <span className="font-semibold text-slate-800">Spiegazione: </span>{q.explanation}
                  </p>
                )}
                {result && (q.section_id || q.source_ids?.length > 0) && <div className="mt-4 flex flex-wrap gap-3" data-testid={`question-links-${qi}`}>
                  {q.section_id && <Link to={`${routePrefix}/reader/${contestId}/${q.section_id}`} className="inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-brand hover:underline" data-testid={`review-section-${qi}`}><BookOpen size={14}/>Ripassa questo concetto →</Link>}
                  {(q.source_ids || []).map((id) => resultSources[id] ? <a key={id} href={resultSources[id].official_url} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-brand hover:underline" data-testid={`review-source-${qi}`}><ExternalLink size={14}/>Consulta la fonte<span className="sr-only"> (si apre in una nuova scheda)</span></a> : null)}
                </div>}
                {result && (q.source_ids || []).map((id) => <SourceAttribution key={id} source={resultSources[id]}/>)}
              </div>
            );
          })}
          {!result && (
            <Button onClick={submit} disabled={answeredCount < questions.length} data-testid="quiz-submit"
              className="w-full h-12 rounded-full bg-brand hover:bg-brand-hover text-white font-semibold sticky bottom-5">
              Consegna ({answeredCount}/{questions.length} risposte)
            </Button>
          )}
        </div>
      )}
    </main>
  );
}
